/* eslint-disable @typescript-eslint/no-non-null-assertion */
import TokenType from '@lib/core/src/enums/TokenType';
import { jwtDecode } from 'jwt-decode';

import { SignInResponse, UserDetailsResponse, UserDetailsType } from '@/api/generated';
import { AXIOS_INSTANCE } from '@/axios/mutator/custom-instance';
import RoutesMap from '@/enums/RoutesMap';

type JourneyJwt = Record<string, string>;
const setRefreshInterceptor = () => {
  AXIOS_INSTANCE.interceptors.request.use(
    request => {
      const idToken = localStorage.getItem(TokenType.ID);
      if (idToken) {
        // eslint-disable-next-line no-param-reassign
        request.headers['Authorization'] = `Bearer ${idToken}`;
      }
      return request;
    },
    error => Promise.reject(error)
  );

  AXIOS_INSTANCE.interceptors.response.use(
    response => response, // Directly return successful responses.
    async error => {
      // If the error is due to a failed login request, do not attempt to refresh the
      // token and redirect to the login page.
      const isLoginPage = window.location.pathname.includes(RoutesMap.Login);
      if (isLoginPage) return Promise.reject(error);

      const originalRequest = error.config;

      if (originalRequest?.skipAuthRefresh) {
        return Promise.reject(error);
      }

      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
        try {
          const refreshToken = localStorage.getItem(TokenType.REFRESH); // Retrieve the stored refresh token.

          const idToken = localStorage.getItem(TokenType.ID); // Retrieve the stored refresh token.

          const email = jwtDecode<JourneyJwt>(idToken!).email; // Extract email from id token.

          if (!idToken || !refreshToken) {
            // If there is no refresh token stored, redirect to the login page.
            window.location.href = RoutesMap.Login;
            return Promise.reject(error);
          }

          // Make a request to your auth server to refresh the token.
          const signInRefreshResponse = await AXIOS_INSTANCE.post<SignInResponse>(
            '/auth/signIn/refresh',
            {
              email,
              refreshToken
            },
            { skipAuthRefresh: true }
          );

          const signInDetails = signInRefreshResponse?.data.items?.[0];
          const accessToken = signInDetails?.accessToken;
          const socketToken = signInDetails?.socketToken;
          const newIdToken = signInDetails?.idToken;

          // Store the new access and refresh tokens.
          localStorage.setItem(TokenType.ID, newIdToken!);
          localStorage.setItem(TokenType.ACCESS, accessToken!);
          localStorage.setItem(TokenType.SOCKET, socketToken!);

          const userDetailsResponse =
            await AXIOS_INSTANCE.get<UserDetailsResponse>('/user/details');

          const userDetails = userDetailsResponse?.data.items?.[0];

          // If the user is a JP, Admin, or Super Admin, sign in to the account.
          if (
            userDetails?.type === UserDetailsType.jp ||
            userDetails?.type === UserDetailsType.admin ||
            userDetails?.type === UserDetailsType.superAdmin
          ) {
            const currentAccount = userDetails?.accounts?.[0];

            const accountSignInResponse = await AXIOS_INSTANCE.post<SignInResponse>(
              '/auth/accountSignIn',
              {
                accountId: Number(currentAccount?.id)
              }
            );

            const accountSignInDetails = accountSignInResponse?.data?.items?.[0];

            // Store the new access and refresh tokens.
            if (accountSignInDetails?.idToken) {
              localStorage.setItem(TokenType.ID, accountSignInDetails.idToken);
            }
          }

          return AXIOS_INSTANCE(originalRequest); // Retry the original request with the new access token.
        } catch (refreshError) {
          // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
          console.error('Token refresh failed:', refreshError);
          localStorage.removeItem(TokenType.ID);
          localStorage.removeItem(TokenType.ACCESS);
          localStorage.removeItem(TokenType.REFRESH);
          localStorage.removeItem(TokenType.SOCKET);
          window.location.href = RoutesMap.Login; // Redirect to the login page.
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error); // For all other errors, return the error as is.
    }
  );
};
export default setRefreshInterceptor;
